import { css } from "@emotion/react";
import { type LoadingSkeletonProps } from "./types";
import { skeletonStyles } from "./style";

/**
 * Renders a skeleton loader, which can be either a single line or multiple lines.
 * It is primarily used to indicate that content is loading and provides a visual placeholder.
 */
export function LoadingSkeleton({
  variant = "line",
  ...props
}: LoadingSkeletonProps) {
  switch (variant) {
    case "multi-line":
      return <LoadingSkeletonMultiLine {...props} />;
    case "line":
    default:
      return <LoadingSkeletonLine {...props} />;
  }
}

function LoadingSkeletonLine({
  children,
  width = children ? "fit-content" : "100%",
  height = children ? "auto" : "1rem",
  maxWidth = "100%",
  duration = 3_000,
  delay = 0
}: Omit<LoadingSkeletonProps, "variant"> & {
  duration?: number;
  delay?: number;
}) {
  return (
    <div
      className="kit-LoadingSkeleton kit-LoadingSkeleton--line"
      data-testid="kit-loading-skeleton-line"
      css={[
        css`
          width: ${width};
          height: ${height};
          max-width: ${maxWidth};
          --skeleton-high-light: #fef7fb;
          --skeleton-low-light: #ebe6ec;
          --skeleton-duration: ${duration}ms;
          --skeleton-delay: ${delay}ms;
        `,
        skeletonStyles.base,
        children && skeletonStyles.withChildren
      ]}
    >
      {children}
    </div>
  );
}

function LoadingSkeletonMultiLine({
  width = "100%",
  height = "1rem",
  maxWidth = "100%"
}: Omit<LoadingSkeletonProps, "variant">) {
  return (
    <div
      className="kit-LoadingSkeleton kit-LoadingSkeleton--multi-line"
      data-testid="kit-loading-skeleton-multi-line"
      css={skeletonStyles.multiLineContainer}
    >
      {Array.from({ length: 3 }).map((_, index) => (
        <LoadingSkeletonLine
          key={index}
          width={index === 0 ? width : `calc(${width} - ${index * 20}%)`}
          height={height}
          maxWidth={maxWidth}
          delay={(index + 1) * 300}
        />
      ))}
    </div>
  );
}
